<template>
  <div class="collection-card-wrapper">
    <div class="image-container">
      <img v-if="src" class="collection-thumb" :src="src" alt="thumb" />
      <div v-else class="default-thumb" />
    </div>
    <div class="collection-details-container" :class="{ 'is-not-title': !isTitle }">
      <div class="collection-details">
        <h4 class="collection-title">{{ cardTitle }}</h4>
        <span v-if="isTitle" class="collection-description">{{ documentName }}</span>
        <div v-else>
          <span>{{ numberOfDocuments }} </span>
          <span class="bullet">•</span>
          <span>{{ formatUpdatedAt }}</span>
        </div>
        <TextBody
          v-if="!isTitle && isSaved"
          tag="span"
          i18n-key="common.added"
          :body-type="3"
          weight="medium"
          color="#6222e3"
        ></TextBody>
      </div>
      <CollectionButton v-if="!isTitle" :document="document" :collection-id="collection.collectionId" />
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n/dist/vue-i18n';
import { calculatePassedTime } from '~/utils/date-utils';
import useCollections from '~/composables/useCollections';
import CollectionButton from '~/components/common/CollectionButton';
import TextBody from '~/components/common/TextBody';

export default {
  props: {
    collection: {
      type: Object,
      default: () => {},
    },
    src: {
      type: String,
      default: '',
    },
    document: {
      type: Object,
      default: () => {},
    },
    isTitle: {
      type: Boolean,
      default: false,
    },
    documentName: {
      type: String,
      default: '',
    },
  },

  components: { CollectionButton, TextBody },

  setup(props) {
    const { t } = useI18n();
    const { isInCollection } = useCollections();
    const formatUpdatedAt = computed(() => calculatePassedTime(props.collection.modifiedAt));
    const isSaved = computed(() =>
      props.collection && props.document
        ? isInCollection(props.collection.collectionId, props.document.externalId).value
        : false,
    );
    const cardTitle = computed(() => (props.isTitle ? 'Add to collection' : props.collection.collectionName));
    const numberOfDocuments = computed(() => `${props.collection.documents.length}  ${t('collections.documents')}`);
    return {
      cardTitle,
      formatUpdatedAt,
      isSaved,
      numberOfDocuments,
    };
  },
};
</script>

<style scoped lang="scss">
.is-not-title {
  border-bottom: 1px solid $bb-neutral-5;
}

.collection-card-wrapper {
  display: flex;
  flex-direction: row;
  gap: $bb-spacing-2;
  align-items: center;

  .default-thumb {
    width: 64px;
    height: 50px;
    background-color: $bb-neutral-5;
  }

  .image-container {
    display: flex;
    flex-direction: column;
    justify-content: space-between; /* Aligns children (start, content, end) */
    border-radius: $bb-spacing-1;
    background-color: $bb-neutral-5;
    width: 64px;
    height: 50px;
    align-items: flex-start;
    gap: $bb-spacing-1;

    img {
      max-width: 60px;
      max-height: 48px;
      align-self: center;
    }
  }

  .collection-details-container {
    display: flex;
    flex-direction: row;
    flex-grow: 1;

    button {
      align-self: center;
    }

    .collection-details {
      flex-grow: 1;
      display: flex;
      flex-direction: column;
      max-width: 100%;

      .collection-description {
        @include bb-medium-text-styles('xs');
        font-weight: 400;
        color: $bb-neutral-1;
      }

      .collection-title {
        @include bb-medium-text-styles('md');
        line-height: $bb-spacing-5;
        color: $bb-primary-3;
        cursor: pointer;
      }

      span {
        @include bb-medium-text-styles('xs');
        color: $bb-neutral-3;
        letter-spacing: 0.1px;
        font-size: 13px;
      }

      .bullet {
        margin: 0 $bb-spacing-1;
      }
    }
  }
}
</style>
